import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import client from "../api/client";
import { useSocket } from "../hooks/useSocket";
import { useAuth } from "../contexts/AuthContext";

interface ScoreboardEntry {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  totalPoints: number;
  solvedCount: number;
  lastSolveAt: string | null;
  rank: number;
  isTeam?: boolean;
}

interface ScoreboardData {
  entries: ScoreboardEntry[];
  totalParticipants: number;
  totalSolves: number;
  isFrozen: boolean;
  competitionEnd: string | null;
}

interface ProgressPoint {
  timestamp: string;
  points: number;
  username: string;
}

interface ProgressData {
  datasets: { username: string; data: { timestamp: string; points: number }[] }[];
}

const LINE_COLORS = [
  "#5b7cff", "#ff6b5c", "#34d399", "#fbbf24",
  "#a78bfa", "#f472b6", "#38bdf8", "#fb923c",
  "#4ade80", "#e879f9",
];

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <div className="flex h-8 w-8 items-center justify-center">
        <svg viewBox="0 0 32 32" className="h-7 w-7">
          <polygon
            points="16,2 20,12 30,12 22,18 25,28 16,22 7,28 10,18 2,12 12,12"
            fill="none"
            stroke="#fbbf24"
            strokeWidth="1.5"
            opacity="0.8"
          />
          <text
            x="16"
            y="18"
            textAnchor="middle"
            fill="#fbbf24"
            fontSize="10"
            fontWeight="bold"
            fontFamily="JetBrains Mono, monospace"
          >
            1
          </text>
        </svg>
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="flex h-8 w-8 items-center justify-center">
        <svg viewBox="0 0 32 32" className="h-7 w-7">
          <polygon
            points="16,4 28,16 16,28 4,16"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="1.5"
            opacity="0.7"
          />
          <text
            x="16"
            y="19"
            textAnchor="middle"
            fill="#94a3b8"
            fontSize="10"
            fontWeight="bold"
            fontFamily="JetBrains Mono, monospace"
          >
            2
          </text>
        </svg>
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="flex h-8 w-8 items-center justify-center">
        <svg viewBox="0 0 32 32" className="h-7 w-7">
          <polygon
            points="16,4 28,16 16,28 4,16"
            fill="none"
            stroke="#c9a869"
            strokeWidth="1.5"
            opacity="0.6"
          />
          <text
            x="16"
            y="19"
            textAnchor="middle"
            fill="#c9a869"
            fontSize="10"
            fontWeight="bold"
            fontFamily="JetBrains Mono, monospace"
          >
            3
          </text>
        </svg>
      </div>
    );
  }
  return (
    <span className="font-mono text-sm text-ds-text-muted w-8 text-center">
      {rank}
    </span>
  );
}

function ProgressChart({ data }: { data: ProgressData }) {
  const [hoveredLine, setHoveredLine] = useState<number | null>(null);

  const allPoints = useMemo(() => {
    return data.datasets.flatMap((ds) => ds.data);
  }, [data]);

  const { minX, maxX, maxY } = useMemo(() => {
    if (allPoints.length === 0) return { minX: 0, maxX: 1, minY: 0, maxY: 100 };
    const timestamps = allPoints.map((p) => new Date(p.timestamp).getTime());
    const points = allPoints.map((p) => p.points);
    return {
      minX: Math.min(...timestamps),
      maxX: Math.max(...timestamps),
      maxY: Math.max(...points) || 100,
    };
  }, [allPoints]);

  const width = 800;
  const height = 300;
  const padX = 50;
  const padY = 20;
  const chartW = width - padX * 2;
  const chartH = height - padY * 2;

  const scaleX = (ts: number) => padX + ((ts - minX) / (maxX - minX || 1)) * chartW;
  const scaleY = (pts: number) => padY + chartH - (pts / maxY) * chartH;

  const buildPath = (points: { timestamp: string; points: number }[]) => {
    return points
      .map((p, i) => {
        const x = scaleX(new Date(p.timestamp).getTime());
        const y = scaleY(p.points);
        return `${i === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");
  };

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[600px]">
        <line
          x1={padX}
          y1={padY + chartH}
          x2={padX + chartW}
          y2={padY + chartH}
          stroke="#1e2a45"
          strokeWidth="1"
        />
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => (
          <g key={frac}>
            <line
              x1={padX}
              y1={scaleY(maxY * frac)}
              x2={padX + chartW}
              y2={scaleY(maxY * frac)}
              stroke="#1e2a45"
              strokeWidth="0.5"
              strokeDasharray="4 4"
            />
            <text
              x={padX - 8}
              y={scaleY(maxY * frac) + 4}
              textAnchor="end"
              fill="#5a6a8a"
              fontSize="10"
              fontFamily="JetBrains Mono, monospace"
            >
              {Math.round(maxY * frac)}
            </text>
          </g>
        ))}
        {data.datasets.map((ds, i) => (
          <path
            key={ds.username}
            d={buildPath(ds.data)}
            fill="none"
            stroke={LINE_COLORS[i % LINE_COLORS.length]}
            strokeWidth={hoveredLine === i ? 2.5 : 1.5}
            opacity={hoveredLine === null || hoveredLine === i ? 1 : 0.3}
            strokeLinejoin="round"
          />
        ))}
      </svg>
      <div className="mt-3 flex flex-wrap gap-3">
        {data.datasets.map((ds, i) => (
          <button
            key={ds.username}
            onMouseEnter={() => setHoveredLine(i)}
            onMouseLeave={() => setHoveredLine(null)}
            className="flex items-center gap-1.5 text-xs transition-opacity hover:opacity-100"
            style={{ opacity: hoveredLine === null || hoveredLine === i ? 1 : 0.4 }}
          >
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: LINE_COLORS[i % LINE_COLORS.length] }}
            />
            <span className="text-ds-text-dim">{ds.username}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ScoreboardPage() {
  const { user } = useAuth();
  const { on, off } = useSocket();
  const [tab, setTab] = useState<"individual" | "teams">("individual");
  const [data, setData] = useState<ScoreboardData | null>(null);
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const endpoint = tab === "individual" ? "/scoreboard" : "/scoreboard/teams";
      const res = await client.get(endpoint);
      if (res.data?.success) setData(res.data.data);
    } catch (err) {
      console.error("Failed to load scoreboard", err);
    }
  }, [tab]);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await client.get("/scoreboard/progress");
      if (res.data?.success) setProgress(res.data.data);
    } catch {
      // progress is optional
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchData(), fetchProgress()]).finally(() => setLoading(false));
  }, [fetchData, fetchProgress]);

  useEffect(() => {
    const handleUpdate = (newData: ScoreboardData) => setData(newData);
    on("scoreboard:update", handleUpdate);
    return () => off("scoreboard:update", handleUpdate);
  }, [on, off]);

  return (
    <div className="ds-page">
      <div className="mb-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="relative">
            <div className="h-2 w-2 rounded-full bg-ds-accent animate-pulse-slow" />
            <div className="absolute inset-0 h-2 w-2 rounded-full bg-ds-accent/40 animate-ping" />
          </div>
          <h1 className="text-2xl font-bold text-ds-text">Scoreboard</h1>
          {data?.isFrozen && (
            <span className="ds-badge bg-ds-warning/10 text-ds-warning border border-ds-warning/20">
              Frozen
            </span>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex rounded-lg border border-ds-border bg-ds-surface p-0.5">
            {(["individual", "teams"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`relative rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                  tab === t
                    ? "text-ds-text"
                    : "text-ds-text-muted hover:text-ds-text-dim"
                }`}
              >
                {tab === t && (
                  <motion.div
                    layoutId="scoreboard-tab"
                    className="absolute inset-0 rounded-md bg-ds-card border border-ds-border"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{t === "individual" ? "Individual" : "Teams"}</span>
              </button>
            ))}
          </div>

          {data && (
            <div className="flex items-center gap-4 text-xs text-ds-text-muted">
              <span>
                <span className="font-mono text-ds-text">{data.totalParticipants}</span> participants
              </span>
              <span>
                <span className="font-mono text-ds-text">{data.totalSolves}</span> solves
              </span>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-ds-card" />
          ))}
        </div>
      ) : !data || data.entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="relative mb-4 h-20 w-20">
            <div className="absolute inset-0 rounded-full border border-ds-border" />
            <div className="absolute inset-4 rounded-full border border-ds-border/50" />
            <div className="absolute inset-8 rounded-full border border-ds-border/30" />
          </div>
          <p className="text-ds-text-muted">No entries yet</p>
        </div>
      ) : (
        <>
          <div className="ds-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ds-border text-ds-text-muted">
                    <th className="px-4 py-3 font-medium">Rank</th>
                    <th className="px-4 py-3 font-medium">
                      {tab === "individual" ? "Player" : "Team"}
                    </th>
                    <th className="px-4 py-3 font-medium text-right">Points</th>
                    <th className="px-4 py-3 font-medium text-right">Solved</th>
                    <th className="px-4 py-3 font-medium text-right">Last Solve</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence mode="popLayout">
                    {data.entries.map((entry, idx) => {
                      const isCurrentUser = user?.id === entry.id;
                      const rowBg =
                        idx === 0
                          ? "bg-gradient-to-r from-yellow-500/5 to-transparent"
                          : idx === 1
                          ? "bg-gradient-to-r from-gray-400/5 to-transparent"
                          : idx === 2
                          ? "bg-gradient-to-r from-amber-600/5 to-transparent"
                          : isCurrentUser
                          ? "bg-ds-accent/5"
                          : idx % 2 === 0
                          ? "bg-ds-surface/30"
                          : "";

                      return (
                        <motion.tr
                          key={entry.id}
                          layout
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className={`border-b border-ds-border/50 transition-colors hover:bg-ds-surface/50 ${rowBg}`}
                        >
                          <td className="px-4 py-3">
                            <RankBadge rank={entry.rank} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ds-accent/10 text-xs font-medium text-ds-accent">
                                {entry.username.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span
                                  className={`font-medium ${
                                    isCurrentUser ? "text-ds-accent" : "text-ds-text"
                                  }`}
                                >
                                  {entry.displayName || entry.username}
                                </span>
                                {entry.displayName && (
                                  <span className="ml-2 text-xs text-ds-text-muted">
                                    @{entry.username}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-ds-accent">
                            {entry.totalPoints.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-ds-text-dim">
                            {entry.solvedCount}
                          </td>
                          <td className="px-4 py-3 text-right text-xs text-ds-text-muted">
                            {formatRelativeTime(entry.lastSolveAt)}
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>

          {progress && progress.datasets.length > 0 && (
            <div className="mt-8">
              <h2 className="ds-section-title mb-4">Score Progression</h2>
              <div className="ds-card p-6">
                <ProgressChart data={progress} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
